import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/input";
import styles from "./AddressSearch.module.scss";

export interface AddressSearchResult {
  display: string;
  lat: number;
  lng: number;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

interface AddressSearchProps {
  label?: string;
  placeholder?: string;
  onSelect: (result: AddressSearchResult) => void;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  label,
  placeholder,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AddressSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setIsLoading(true);
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=0&limit=6&countrycodes=tr&q=${encodeURIComponent(q)}`,
        {
          signal: abortRef.current.signal,
          headers: { "Accept-Language": "tr" },
        },
      )
        .then((r) => r.json())
        .then((data: NominatimResult[]) => {
          const mapped: AddressSearchResult[] = Array.isArray(data)
            ? data.map((d) => ({
                display: d.display_name,
                lat: Number(d.lat),
                lng: Number(d.lon),
              }))
            : [];
          setResults(mapped);
          setIsOpen(true);
        })
        .catch(() => {
          // ignore abort / network error
        })
        .finally(() => setIsLoading(false));
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <Input
        label={label ?? t("addressSearch.label")}
        placeholder={
          placeholder ??
          t("addressSearch.placeholder")
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setIsOpen(true)}
      />
      {isOpen && (
        <ul className={styles.results}>
          {isLoading && (
            <li className={styles.statusRow}>
              {t("common.loading")}
            </li>
          )}
          {!isLoading && results.length === 0 && (
            <li className={styles.statusRow}>
              {t("addressSearch.noResults")}
            </li>
          )}
          {!isLoading &&
            results.map((r, i) => (
              <li
                key={`${r.lat}-${r.lng}-${i}`}
                className={styles.resultRow}
                onClick={() => {
                  onSelect(r);
                  setIsOpen(false);
                  setQuery("");
                }}
              >
                {r.display}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default AddressSearch;
